import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong, faPhoneVolume, faCircleCheck, faStar, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import "./Header.scss";
import { NavLink } from "react-router-dom";

// "30 jours gratuits" is left out here: the promo strip above the navbar already says it
const trustKeys = [
  "landing.header.trust_no_commitment",
  "landing.header.trust_migration",
  "landing.header.trust_secure_data",
];

// Same pharmacies (initials + colors) as the testimonials section below
const proofAvatars = [
  { initials: "PA", color: "#2563eb" },
  { initials: "PB", color: "#7c3aed" },
  { initials: "PB", color: "#059669" },
];

// Landing hero, laid out like the big SaaS sites: everything centered — headline, subtitle,
// the two calls to action, social proof and the trust line — then a real screenshot of the
// app in a browser frame underneath (with two floating "what it does" cards), fading out at
// the bottom.
// `scheduleCall`: opens the call-booking modal — secondary outline button next to the trial CTA
const Header = ({ scheduleCall }) => {
  const { t, i18n } = useTranslation();
  const heroRef = useRef(null);
  const gridRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Cursor-reactive parallax: mouseX/Y track the pointer as -1..1 across the hero, smoothed
  // through a spring so it drifts rather than snaps. Each background layer reads a different
  // slice of that same value (opposite direction + different magnitude per orb) to fake depth.
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { stiffness: 60, damping: 20, mass: 0.6 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  const orbFarX = useTransform(smoothX, [-1, 1], [-32, 32]);
  const orbFarY = useTransform(smoothY, [-1, 1], [-22, 22]);
  const orbNearX = useTransform(smoothX, [-1, 1], [24, -24]);
  const orbNearY = useTransform(smoothY, [-1, 1], [18, -18]);
  const gridX = useTransform(smoothX, [-1, 1], [-10, 10]);
  const gridY = useTransform(smoothY, [-1, 1], [-8, 8]);

  // Scroll-linked screenshot: starts tilted back in 3D and slightly smaller, then flattens to
  // face the visitor over the first ~500px of scroll; the two floating cards drift up at
  // different speeds (parallax) on their own layers, so their CSS bobbing animation is untouched.
  const { scrollY } = useScroll();
  const smoothScrollY = useSpring(scrollY, { stiffness: 140, damping: 28, mass: 0.4 });
  const shotRotateX = useTransform(smoothScrollY, [0, 500], [18, 0]);
  const shotScale = useTransform(smoothScrollY, [0, 500], [0.93, 1]);
  const floatSaleY = useTransform(smoothScrollY, [0, 700], [0, -70]);
  const floatExpiryY = useTransform(smoothScrollY, [0, 700], [0, -150]);

  const handleMouseMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    mouseX.set(px * 2 - 1);
    mouseY.set(py * 2 - 1);
    if (gridRef.current) {
      gridRef.current.style.setProperty('--spot-x', `${px * 100}%`);
      gridRef.current.style.setProperty('--spot-y', `${py * 100}%`);
      gridRef.current.classList.add('syd-header__bg-grid--active');
    }
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    gridRef.current?.classList.remove('syd-header__bg-grid--active');
  };

  return (
    <header
      className="syd-header"
      id="hero"
      ref={heroRef}
      onMouseMove={prefersReducedMotion ? undefined : handleMouseMove}
      onMouseLeave={prefersReducedMotion ? undefined : handleMouseLeave}
    >
      {/* Background decorations */}
      <div className="syd-header__bg">
        <motion.div className="syd-header__bg-orb-parallax" style={{ x: orbFarX, y: orbFarY }}>
          <div className="syd-header__bg-orb syd-header__bg-orb--1" />
        </motion.div>
        <motion.div className="syd-header__bg-orb-parallax" style={{ x: orbNearX, y: orbNearY }}>
          <div className="syd-header__bg-orb syd-header__bg-orb--2" />
        </motion.div>
        <motion.div
          className="syd-header__bg-grid"
          ref={gridRef}
          style={prefersReducedMotion ? undefined : { x: gridX, y: gridY }}
        />
      </div>

      <div className="container-fluid px-4 px-lg-5">
        <div className="syd-header__inner">

          <motion.h1
            className="syd-header__headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
          >
            {t("landing.header.headline_pre")}
            <br />
            {t("landing.header.headline_with")}{" "}
            <span className="syd-header__accent">
              {t("landing.header.headline_accent")}
              <svg className="syd-header__accent-svg" viewBox="0 0 200 12" preserveAspectRatio="none">
                <path d="M2 8 Q50 2 100 6 Q150 10 198 4" stroke="#2563eb" strokeWidth="3" fill="none" strokeLinecap="round"/>
              </svg>
            </span>
          </motion.h1>

          <motion.p
            className="syd-header__sub"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            {/* **…** in the translation marks the words shown in bold */}
            {t("landing.header.sub").split(/\*\*(.+?)\*\*/).map((part, i) =>
              i % 2 ? <strong key={i}>{part}</strong> : part
            )}
          </motion.p>

          {/* primary call to action (filled) + secondary one (outline), trust line underneath */}
          <motion.div
            className="syd-header__actions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.6 }}
          >
            <NavLink to={`/${i18n.language}/register`} className="syd-header__btn-primary">
              {t("landing.header.cta_primary")}
              <FontAwesomeIcon icon={faArrowRightLong} />
            </NavLink>
            {scheduleCall && (
              <button type="button" className="syd-header__btn-secondary" onClick={scheduleCall}>
                <FontAwesomeIcon icon={faPhoneVolume} />
                {t("landing.freetrial.demo_cta")}
              </button>
            )}
          </motion.div>

          {/* Real screenshot of the app in a browser frame */}
          <motion.div
            className="syd-header__shot"
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <motion.div
              className="syd-header__shot-frame"
              style={prefersReducedMotion ? undefined : { rotateX: shotRotateX, scale: shotScale }}
            >
              <div className="syd-header__shot-bar" aria-hidden="true">
                <span className="syd-header__shot-dots"><i /><i /><i /></span>
                <span className="syd-header__shot-url">saydalani.com</span>
              </div>
              <img
                src="/images/home/hero-cart-screenshot-v3.png"
                alt={t("landing.header.showcase_eyebrow")}
                className="syd-header__shot-img"
              />
            </motion.div>

            {/* floating cards: two concrete things the app does (desktop only) */}
            <motion.div className="syd-header__float-layer" style={prefersReducedMotion ? undefined : { y: floatSaleY }}>
              <div className="syd-header__float syd-header__float--sale" aria-hidden="true">
                <span className="syd-header__float-icon syd-header__float-icon--green">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </span>
                <span>
                  <strong>{t("landing.header.card_sale_title")}</strong>
                  <small>{t("landing.header.card_sale_value")}</small>
                </span>
              </div>
            </motion.div>
            <motion.div className="syd-header__float-layer" style={prefersReducedMotion ? undefined : { y: floatExpiryY }}>
              <div className="syd-header__float syd-header__float--expiry" aria-hidden="true">
                <span className="syd-header__float-icon syd-header__float-icon--amber">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </span>
                <span>
                  <strong>{t("landing.header.card_expiry_title")}</strong>
                  <small>{t("landing.header.card_expiry_value")}</small>
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </header>
  );
};

export default Header;
